import { Component, Inject, OnInit, inject } from '@angular/core';

import { Router, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from  '@angular/material/select' ;
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { LucideAngularModule, Bot, ArrowLeft, Settings2, Trash2, GripVertical } from 'lucide-angular';
import { MatTabsModule } from '@angular/material/tabs';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';

import { Chatbot } from '../../../core/models/chatbot.model';

import { ChatbotManagementService } from '../../../core/services/chatbot-management.service';

export interface ChatbotEditModalData {
  chatbot: Chatbot | null;
}

@Component({
  selector: 'app-chatbot-edit-modal',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    LucideAngularModule,
    RouterModule,
    MatTabsModule,
    MatSelectModule,
    DragDropModule
],
  templateUrl: './chatbot-edit-modal.component.html',
  styleUrls: ['./chatbot-edit-modal.component.scss']
})
export class ChatbotEditModalComponent implements OnInit {
    // íconos de lucide angular
    readonly Bot = Bot;
    readonly ArrowLeft = ArrowLeft;
    readonly Settings2 = Settings2;
    readonly GripVertical = GripVertical;
    readonly Trash2 = Trash2;

    readonly router = inject(Router);

    chatbotForm: FormGroup | null = null;

    // FormArray para los query params (key-value pairs)
    get conversationVisualizerParams(): FormArray {
        return this.chatbotForm?.get('conversationVisualizerParams') as FormArray;
    }

    isEditMode: boolean = false;
    saving: boolean = false;
    loading: boolean = false;
    existingCodes: Set<string> = new Set();

    constructor(
        public dialogRef: MatDialogRef<ChatbotEditModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ChatbotEditModalData,
        private fb: FormBuilder,
        private chatbotService: ChatbotManagementService,
        private snackBar: MatSnackBar
    ) {
        this.isEditMode = !!data.chatbot;
        
        // Cargar códigos existentes para validación
        if (!this.isEditMode) {
            this.loadExistingCodes();
        }
    }

    ngOnInit(): void {
        this.initForm();
    }

    /** Cargar códigos existentes para validación en modo creación */
    private loadExistingCodes(): void {
        this.chatbotService.getAllChatbots().subscribe({
            next: (chatbots) => {
                this.existingCodes = new Set(chatbots.map(c => c.code.toLowerCase()));
            },
            error: (error) => {
                console.error('Error loading existing codes', error);
            }
        });
    }

    /** Form */
    private initForm(): void {
        if (this.isEditMode && this.data.chatbot) {
            // Modo edición: inicializar con datos del usuario
            const paramsArray = this.fb.array([]) as FormArray;
            // El backend devuelve conversationVisualizerParams en snake_case
            const params = (this.data.chatbot as any).conversationVisualizerParams;
            
            if (Array.isArray(params) && params.length > 0) {
                params.forEach((param: any) => {
                    paramsArray.push(this.fb.group({
                        label: [param.label || ''],
                        queryParam: [param.queryParam || ''],
                        dataType: [param.dataType || 'string']
                    }, { validators: this.paramStructureValidator }));
                });
            }
            this.chatbotForm = this.fb.group({
                name: [this.data.chatbot.name],
                description: [this.data.chatbot.description],
                endpointToSend: [this.data.chatbot.endpointToSend],
                credentials: [this.data.chatbot.credentials],
                code: [{ value: this.data.chatbot.code, disabled: true }],
                isActive: [this.data.chatbot.isActive],
                conversationVisualizerParams: paramsArray,
                conversationVisualizerEndpoint: [this.data.chatbot.conversationVisualizerEndpoint],
                conversationVisualizerCredentials: [this.data.chatbot.conversationVisualizerCredentials]
            }, { validators: this.conversationVisualizerValidator });
        } else {
            // Modo creación: formulario vacío con validación de código duplicado
            this.chatbotForm = this.fb.group({
                name: ['', [ Validators.required ]],
                code: ['', [ Validators.required, this.codeFormatValidator()]],            
                description: ['', [ Validators.required ]],
                endpointToSend: ['', [ Validators.required ]],
                credentials: ['', [ Validators.required]],
                isActive: [{ value: true, disabled: true }],
                conversationVisualizerEndpoint: [''], 
                conversationVisualizerCredentials: [''],
                conversationVisualizerParams: this.fb.array([])
            }, { validators: this.conversationVisualizerValidator });
        }
    }

    // Simulación de guardar: imprime los datos del formulario y cierra el modal
    onSaveConsoleLog(): void {
        if (!this.chatbotForm?.valid) {
            return;
        }

        this.saving = true;
        const formValue = this.chatbotForm.getRawValue();

        // visualizar console logs y luego cerrar modal
        this.saving = false;
        this.dialogRef.close({
            ...formValue,
            conversationVisualizerParams: this.conversationVisualizerParams.value
        });        
    }

    // Guardar chatbot (creación o edición)
    onSave(): void {
        if (!this.chatbotForm?.valid) {
            return;
        }

        this.saving = true;
        this.chatbotForm?.get('name')?.disable({ onlySelf: true });
        this.chatbotForm?.get('description')?.disable({ onlySelf: true });
        this.chatbotForm?.get('code')?.disable({ onlySelf: true });
        this.chatbotForm?.get('endpointToSend')?.disable({ onlySelf: true });
        this.chatbotForm?.get('credentials')?.disable({ onlySelf: true });
        this.chatbotForm?.get('conversationVisualizerEndpoint')?.disable({ onlySelf: true });
        this.chatbotForm?.get('conversationVisualizerCredentials')?.disable({ onlySelf: true });
        // Deshabilitar todos los parámetros dinámicos
        const paramsArray = this.chatbotForm?.get('conversationVisualizerParams') as FormArray;
        if (paramsArray) {
            paramsArray.controls.forEach(ctrl => ctrl.disable({ onlySelf: true }));
        }

        const formValue = this.chatbotForm.getRawValue();
        this.sanitizeFormValue(formValue);
        const chatbotData = this.prepareChatbotData(formValue);

        if (this.isEditMode && this.data.chatbot) {
            this.updateChatbot(chatbotData);
        } else {
            this.createChatbot(chatbotData);
        }
    }

    private sanitizeFormValue(formValue: any): void {
        if (formValue.conversationVisualizerCredentials === '') {
            formValue.conversationVisualizerCredentials = null;
        }
        if (formValue.conversationVisualizerEndpoint === '') {
            formValue.conversationVisualizerEndpoint = null;
        }
    }

    private prepareChatbotData(formValue: any): Partial<Chatbot> {
        // Filtrar parámetros vacíos y asegurar que solo se envíen los completos
        const filteredParams = formValue.conversationVisualizerParams?.filter((param: any) => 
            param.label && param.queryParam && param.dataType
        ) || [];

        // Mapear a snake_case para el backend
        const paramsForBackend = filteredParams.map((param: any) => ({
            label: param.label,
            queryParam: param.queryParam,
            dataType: param.dataType
        }));

        const chatbotData = {
            code: formValue.code,
            name: formValue.name,
            description: formValue.description,
            endpointToSend: formValue.endpointToSend,
            endpointToReceive: formValue.endpointToSend,
            credentials: formValue.credentials,
            typeId: 1,
            isActive: formValue.isActive,
            conversationVisualizerEndpoint: formValue.conversationVisualizerEndpoint,
            conversationVisualizerCredentials: formValue.conversationVisualizerCredentials,
            conversationVisualizerParams: paramsForBackend.length > 0 ? paramsForBackend : null
        } as any;

        return chatbotData;
    }

    private updateChatbot(chatbotData: Partial<Chatbot>): void {
        this.chatbotService.updateChatbot(this.data.chatbot!.chatbotId, chatbotData)
            .subscribe({
                next: (result) => {
                    this.saving = false;
                    this.showSuccess('Chatbot actualizado exitosamente.');
                    this.dialogRef.close(result);
                },
                error: (error) => {
                    this.saving = false;
                    this.showError(this.getErrorMessage(error));
                }
            });
    }

    private createChatbot(chatbotData: Partial<Chatbot>): void {
        this.chatbotService.createChatbot(chatbotData)
            .subscribe({
                next: (result) => {
                    this.saving = false;
                    this.showSuccess('Chatbot creado exitosamente.');
                    this.dialogRef.close(result);
                },
                error: (error) => {
                    this.saving = false;
                    this.handleCreateError(error, chatbotData.code);
                }
            });
    }

    private handleCreateError(error: any, code?: string): void {
        if (code && (error.status === 409 || this.existingCodes.has(code.toLowerCase()))) {
            this.showError('El código ingresado ya existe. Por favor, elija otro código.');
        } else {
            this.showError(this.getErrorMessage(error));
        }
    }

    onCancel(): void {
        this.dialogRef.close(null);
    }


    /** Mensajes */
    private getErrorMessage(error: any): string {
        if (error.error?.error) {
        return error.error.error;
        }
        if (error.message) {
        return error.message;
        }
        return 'Error al guardar los cambios. Por favor, intente nuevamente.';
    }

    private showSuccess(message: string): void {
        this.snackBar.open(message, 'Cerrar', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['success-snackbar']
        });
    }

    private showError(message: string): void {
        this.snackBar.open(message, 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
        });
    }

    /** Validador de formato de código (letras, números, guion bajo, guion medio) */
    private codeFormatValidator(): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null; // Si está vacío, dejar que Validators.required lo maneje
            }
            const codePattern = /^[a-zA-Z0-9_-]+$/;
            const isValid = codePattern.test(control.value);
            return isValid ? null : { invalidCodeFormat: true };
        };
    }
    /** Util */
    formatDate(dateString: string | undefined): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-CL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }
    
    goBack(): void {
        this.dialogRef.close(null);
    }


    // Agrega un nuevo parámetro a la lista con la estructura requerida
    addParameter(): void {
        const paramGroup = this.fb.group({
            label: [''],
            queryParam: [''],
            dataType: ['string'] // valor por defecto
        }, { validators: this.paramStructureValidator });
        this.conversationVisualizerParams.push(paramGroup);
    }

    // Valida que todos los campos estén completos o todos vacíos
    paramStructureValidator(group: FormGroup) {
        const label = group.get('label')?.value;
        const queryParam = group.get('queryParam')?.value;
        const dataType = group.get('dataType')?.value;
        const allEmpty = !label && !queryParam && !dataType;
        const allFilled = !!label && !!queryParam && !!dataType;
        return (allEmpty || allFilled) ? null : { paramStructure: true };
    }

    // Valida que endpoint y credentials de conversation visualizer estén ambos completos o ambos vacíos
    conversationVisualizerValidator(group: FormGroup) {
        const endpoint = group.get('conversationVisualizerEndpoint')?.value;
        const credentials = group.get('conversationVisualizerCredentials')?.value;
        if ((!endpoint && !credentials) || (endpoint && credentials)) {
            return null; // válido
        }
        return { conversationVisualizerIncomplete: true }; // inválido
    }
    

    // elimina la etiqueta en el índice especificado
    removeParameter(index: number): void {
        this.conversationVisualizerParams.removeAt(index);
    }

    // Maneja el evento de drop para reordenar los parámetros
    dropParameter(event: CdkDragDrop<any[]>): void {
        const params = this.conversationVisualizerParams;
        const item = params.at(event.previousIndex);
        params.removeAt(event.previousIndex);
        params.insert(event.currentIndex, item);
    }
}
